/* eslint-disable react/prop-types */
"use client";

import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "../../api/axios";
import { MessageBubble } from "./ChatFeed";
import {
  CHAT_REACTION_OPTIONS,
  isSupportedChatReaction,
  normalizeMessageReactions,
} from "../../utils/chatReactions";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import MobileMessageActionLayer from "./MobileMessageActionLayer";

// ChatOverlay renders:
// 1) Context actions menu overlay (Copy / Reply / Edit / Delete)
// 2) Image viewer overlay (tap outside to close + swipe down to close)
//
// It is intentionally UI-only: it relies on props from Chat.jsx for state + actions.

const REACTION_DETAIL_ORDER = new Map(
  CHAT_REACTION_OPTIONS.map((option, index) => [option.emoji, index])
);

const EMPTY_REACTION_PANEL = {
  messageId: null,
  loading: false,
  groups: [],
  count: 0,
  error: null,
};

function buildOverlayAvatarSrc(user) {
  return user?.avatarSrc || resolveUserAvatarSrc(user);
}

function normalizeReactionDetailGroups(payload) {
  const rawGroups = Array.isArray(payload?.reactions)
    ? payload.reactions
    : Array.isArray(payload)
      ? payload
      : [];

  return rawGroups
    .map((group) => {
      const emoji = String(group?.emoji || "").trim();
      if (!isSupportedChatReaction(emoji)) return null;

      const rawUsers = Array.isArray(group?.users) ? group.users : [];
      const users = rawUsers
        .map((user) => {
          const usernameRaw =
            user?.username ||
            user?.user_username ||
            user?.author_username ||
            user?.handle ||
            "";

          const username = usernameRaw ? String(usernameRaw) : "";
          const code = (user?.code || user?.user_code || "").toString();

          return {
            id: user?.id ?? user?.user_id ?? null,
            user_id: user?.user_id ?? user?.id ?? null,
            code: code || null,
            username: username || null,
            is_self: !!(user?.is_self || user?.is_me || user?.me),
            avatarSrc: buildOverlayAvatarSrc(user),
            has_premium_subscription: Boolean(user?.has_premium_subscription),
          };
        })
        .filter(Boolean);

      const count = Math.max(
        Number(group?.count || 0) || 0,
        users.length
      );

      if (count <= 0 && !users.length) return null;

      return {
        emoji,
        count,
        users,
      };
    })
    .filter(Boolean)
    .sort(
      (left, right) =>
        (REACTION_DETAIL_ORDER.get(left?.emoji) ?? Number.MAX_SAFE_INTEGER) -
        (REACTION_DETAIL_ORDER.get(right?.emoji) ?? Number.MAX_SAFE_INTEGER)
    );
}

function filterSeenUsers(rawUsers, currentAuthorId) {
  const list = Array.isArray(rawUsers) ? rawUsers : [];

  return list.filter((user) => {
    const uid = user?.id ?? user?.user_id ?? user?.userId ?? null;
    const isSelfFlag = !!(user?.is_self || user?.is_me || user?.me);
    const isAuthor =
      currentAuthorId != null && uid != null && String(uid) === String(currentAuthorId);

    return !isSelfFlag && !isAuthor;
  });
}

export default function ChatOverlay({
  // Context menu
  ctxMenu,
  onCloseCtxMenu,
  copyMessage,
  beginReply,
  beginEdit,
  deleteMessage,
  onTogglePin,
  pinnedMessageIds = [],
  pinnedMessageId = null,
  onToggleReaction,
  canEdit,
  canDelete,
  onToggleSeen,
  seenPanel,

  // Variant: "event" (group/event chats) or "direct" (DM)
  variant = "event",

  // Position helpers (from Chat.jsx)
  getHeaderBottomPx,

  // Image viewer
  imageViewer,
  onCloseImageViewer,

  // i18n
  t,

  // Desktop overlay scope
  restrictBackdropToParent = false,
}) {
  const hasCtx = !!ctxMenu?.item;
  const hasImage = !!imageViewer?.src;
  const imageMessage = imageViewer?.item || null;
  const canDeleteImage = Boolean(imageMessage?.is_self && canDelete?.(imageMessage));
  const isDirect = variant === "direct";
  const isMobileContextLayer = !restrictBackdropToParent;
  const [detailPanelKind, setDetailPanelKind] = useState(null);
  const [reactionFilterEmoji, setReactionFilterEmoji] = useState(null);
  const [reactionPanel, setReactionPanel] = useState(EMPTY_REACTION_PANEL);
  const reactionCacheRef = useRef({});

  const resetReactionPanel = useCallback(() => {
    setReactionPanel(EMPTY_REACTION_PANEL);
  }, []);

  const fetchReactionDetails = useCallback(async (messageId) => {
    if (!messageId) return { groups: [], count: 0 };

    const resp = await api.get(`/chats/internal/messages/${messageId}/reactions/`);
    const groups = normalizeReactionDetailGroups(resp?.data);
    const count =
      Number(resp?.data?.count || 0) ||
      groups.reduce((sum, group) => sum + Number(group?.count || 0), 0);

    reactionCacheRef.current[String(messageId)] = { groups, count };
    return { groups, count };
  }, []);

  const ensureReactionDetails = useCallback(
    async (messageId) => {
      if (!messageId) return;

      const cacheKey = String(messageId);
      const cached = reactionCacheRef.current[cacheKey];

      setReactionPanel({
        messageId,
        loading: !cached,
        groups: cached?.groups || [],
        count: cached?.count || 0,
        error: null,
      });

      if (cached) return;

      try {
        const { groups, count } = await fetchReactionDetails(messageId);
        setReactionPanel((prev) =>
          String(prev?.messageId) === cacheKey
            ? {
                messageId,
                loading: false,
                groups,
                count,
                error: null,
              }
            : prev
        );
      } catch {
        setReactionPanel((prev) =>
          String(prev?.messageId) === cacheKey
            ? {
                messageId,
                loading: false,
                groups: [],
                count: 0,
                error: "failed",
              }
            : prev
        );
      }
    },
    [fetchReactionDetails]
  );

  const currentMessageId = ctxMenu?.item?.id || null;
  const currentAuthorId =
    ctxMenu?.item?.author_id ??
    ctxMenu?.item?.authorId ??
    ctxMenu?.item?.user_id ??
    ctxMenu?.item?.userId ??
    ctxMenu?.item?.sender_id ??
    ctxMenu?.item?.senderId ??
    null;

  const messageReactions = useMemo(
    () => normalizeMessageReactions(ctxMenu?.item?.reactions),
    [ctxMenu?.item?.reactions]
  );

  const seenUsers = useMemo(() => {
    const isForThis = String(seenPanel?.messageId) === String(currentMessageId);
    const list = (isForThis && (seenPanel?.users || seenPanel?.results)) || [];

    return filterSeenUsers(list, currentAuthorId);
  }, [currentAuthorId, currentMessageId, seenPanel]);

  const seenPreviewUsers = useMemo(
    () =>
      filterSeenUsers(
        ctxMenu?.item?.seen_users || ctxMenu?.item?.seenUsers || [],
        currentAuthorId
      ),
    [currentAuthorId, ctxMenu?.item?.seenUsers, ctxMenu?.item?.seen_users]
  );

  const seenCount = useMemo(() => {
    const hasLiveSeenList =
      String(seenPanel?.messageId) === String(currentMessageId) &&
      Array.isArray(seenPanel?.users || seenPanel?.results);
    if (hasLiveSeenList) return seenUsers.length;

    const hasPreviewSeenList = Array.isArray(
      ctxMenu?.item?.seen_users || ctxMenu?.item?.seenUsers
    );
    if (hasPreviewSeenList) return seenPreviewUsers.length;

    const raw =
      (String(seenPanel?.messageId) === String(currentMessageId)
        ? seenPanel?.count
        : null) ??
      ctxMenu?.item?.seen_count ??
      ctxMenu?.item?.seenCount ??
      0;

    return Math.max(0, Number(raw || 0) || 0);
  }, [
    currentMessageId,
    ctxMenu?.item?.seenUsers,
    ctxMenu?.item?.seen_users,
    ctxMenu?.item?.seenCount,
    ctxMenu?.item?.seen_count,
    seenPanel,
    seenPreviewUsers.length,
    seenUsers.length,
  ]);

  const reactionGroups = useMemo(() => {
    if (String(reactionPanel?.messageId) !== String(currentMessageId)) return [];
    return Array.isArray(reactionPanel?.groups) ? reactionPanel.groups : [];
  }, [currentMessageId, reactionPanel]);

  const visibleReactionGroups = useMemo(() => {
    if (!reactionFilterEmoji) return reactionGroups;
    return reactionGroups.filter((group) => group?.emoji === reactionFilterEmoji);
  }, [reactionFilterEmoji, reactionGroups]);

  useEffect(() => {
    if (!hasCtx) {
      setDetailPanelKind(null);
      setReactionFilterEmoji(null);
      resetReactionPanel();
    }
  }, [hasCtx, resetReactionPanel]);

  useEffect(() => {
    setDetailPanelKind(null);
    setReactionFilterEmoji(null);
    resetReactionPanel();
  }, [ctxMenu?.item?.id, resetReactionPanel]);

  useEffect(() => {
    if (!hasCtx || !currentMessageId) return;
    if (ctxMenu?.openDetailPanel !== "reactions") return;

    setDetailPanelKind("reactions");
    setReactionFilterEmoji(
      isSupportedChatReaction(ctxMenu?.reactionEmoji) ? ctxMenu.reactionEmoji : null
    );
    void ensureReactionDetails(currentMessageId);
  }, [
    ctxMenu?.openDetailPanel,
    ctxMenu?.reactionEmoji,
    currentMessageId,
    ensureReactionDetails,
    hasCtx,
  ]);

  // =========================
  // Context menu positioning (hook must be called unconditionally!)
  // =========================
  const ctxLayout = useMemo(() => {
    if (!hasCtx) return null;

    // ---- Build actions (dynamic) ----
    const messageId = ctxMenu?.item?.id;
    const canViewSeen = !!ctxMenu?.item?.can_view_seen;
    const canReact =
      !!ctxMenu?.item?.id &&
      !ctxMenu?.item?.is_deleted &&
      (ctxMenu?.item?.message_type == null ||
        ctxMenu?.item?.message_type === "text" ||
        ctxMenu?.item?.message_type === "sticker" ||
        ctxMenu?.item?.message_type === "system_as_user");

    const actions = [
      {
        key: "copy",
        label: t("chats.event_chat.ctx.copy"),
        onClick: () => {
          copyMessage?.(ctxMenu.item?.text);
          onCloseCtxMenu?.();
        },
        icon: "/assets/menu/chat_ctx_copy.svg",
      },
      {
        key: "reply",
        label: t("chats.event_chat.ctx.reply"),
        onClick: () => beginReply?.(ctxMenu.item),
        icon: "/assets/menu/chat_ctx_reply.svg",
      },
    ];

    if (canReact && typeof onTogglePin === "function") {
      const isPinned = pinnedMessageIds.some((id) => Number(id) === Number(messageId)) ||
        Number(pinnedMessageId) === Number(messageId);
      actions.push({
        key: "pin",
        label: t(isPinned ? "chats.event_chat.pinned.unpin" : "chats.event_chat.pinned.pin", {
          defaultValue: isPinned ? "Unpin" : "Pin",
        }),
        onClick: () => {
          onTogglePin(ctxMenu.item);
          onCloseCtxMenu?.();
        },
        iconNode: (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m8 3 8 0-1 5 3 3v2H6v-2l3-3-1-5ZM12 13v8" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      });
    }

    // Seen: only for my own messages (Telegram-like) — but NOT in direct chats
    if (canViewSeen && !isDirect) {
      actions.push({
        key: "seen",
        label: t("chats.event_chat.seen.label", { count: seenCount }),
        onClick: () => {
          setReactionFilterEmoji(null);
          setDetailPanelKind((current) => (current === "seen" ? null : "seen"));
          onToggleSeen?.(messageId, { force: true });
        },
        iconNode: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="opacity-90"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.9"
            />
            <path
              d="M12 16V12"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 8H12.01"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ),
      });
    }

    if (messageReactions.length > 0) {
      actions.push({
        key: "reactions",
        label: t("chats.event_chat.reactions.details"),
        onClick: () => {
          const nextKind = detailPanelKind === "reactions" ? null : "reactions";
          setReactionFilterEmoji(null);
          setDetailPanelKind(nextKind);
          if (nextKind === "reactions") {
            void ensureReactionDetails(messageId);
          }
        },
        iconNode: (
          <span className="text-[15px] leading-none" aria-hidden="true">
            {messageReactions[0]?.emoji || "🙂"}
          </span>
        ),
      });
    }

    if (canEdit?.(ctxMenu.item)) {
      actions.push({
        key: "edit",
        label: t("chats.event_chat.ctx.edit"),
        onClick: () => beginEdit?.(ctxMenu.item),
        icon: "/assets/menu/chat_ctx_edit.svg",
      });
    }

    const showDelete = !!canDelete?.(ctxMenu.item);
    if (showDelete) {
      actions.push({
        key: "delete",
        label: t("chats.event_chat.ctx.delete"),
        onClick: () => deleteMessage?.(ctxMenu.item),
        icon: "/assets/menu/chat_ctx_delete.svg",
        danger: true,
      });
    }

    // ---- Dimensions ----
    const MENU_W = 144; // matches w-36
    const REACTION_W =
      CHAT_REACTION_OPTIONS.length * 32 +
      Math.max(0, CHAT_REACTION_OPTIONS.length - 1) * 4 +
      16;
    const REACTION_H = 44;
    const DETAIL_W = detailPanelKind === "reactions" ? 224 : 180;
    const DETAIL_H = detailPanelKind === "reactions" ? 236 : 180;
    const ROW_H = 30;
    const DIVIDER_H = 1;
    const dividers = Math.max(0, actions.length - 1) * DIVIDER_H;
    const MENU_H = actions.length * ROW_H + dividers;

    const GAP = 8;
    const REACTION_GAP = 0;
    const PAD = 12;

    // ---- Viewport (VisualViewport-aware) ----
    const vv = window.visualViewport;
    const vw = vv?.width || window.innerWidth;
    const vh = vv?.height || window.innerHeight;
    const vTop = vv?.offsetTop || 0;
    const vLeft = vv?.offsetLeft || 0;
    const scopeRect = restrictBackdropToParent ? ctxMenu?.overlayScopeRect : null;
    const feedRect = restrictBackdropToParent ? ctxMenu?.overlayFeedRect : null;
    const safeLeft = Math.max(vLeft + PAD, (scopeRect?.left ?? vLeft) + PAD);
    const safeRight = Math.min(vLeft + vw - PAD, (scopeRect?.right ?? vLeft + vw) - PAD);

    const headerBottom =
      typeof getHeaderBottomPx === "function" ? getHeaderBottomPx() : 0;
    const safeTop = Math.max(vTop + PAD, headerBottom + PAD, (feedRect?.top ?? 0) + PAD);
    const safeBottomRaw = Math.min(
      vTop + vh - PAD,
      (scopeRect?.bottom ?? vTop + vh) - PAD,
      (feedRect?.bottom ?? vTop + vh) - PAD
    );
    const safeBottom = Math.max(safeTop + 40, safeBottomRaw);

    const rowRect = ctxMenu?.rowRect || ctxMenu?.rect;
    const bubbleRect = ctxMenu?.bubbleRect || ctxMenu?.rect;

    if (!rowRect || !bubbleRect) return null;

    const clamp = (x, a, b) => Math.min(Math.max(x, a), b);

    const availableMessageHeight = Math.max(80, safeBottom - safeTop);
    const msgHeight = Math.min(rowRect.height, availableMessageHeight);
    const msgTop = clamp(rowRect.top, safeTop, safeBottom - msgHeight);
    const msgLeft = clamp(
      rowRect.left,
      safeLeft,
      Math.max(safeLeft, safeRight - rowRect.width)
    );

    // The cloned row may be clamped into the safe viewport area.
    // To keep the actions menu consistently positioned relative to the CLONED bubble,
    // shift the original bubbleRect by the same delta as the row.
    const dx = msgLeft - rowRect.left;
    const dy = msgTop - rowRect.top;

    const bubbleRectAdj = {
      top: Math.max(bubbleRect.top + dy, msgTop),
      bottom: Math.min(bubbleRect.bottom + dy, msgTop + msgHeight),
      left: bubbleRect.left + dx,
      right: bubbleRect.right + dx,
    };

    // The desktop composer occupies the bottom of the chat. Keep the message
    // actions in the visible feed and use the free lane beside a large bubble.
    if (restrictBackdropToParent) {
      const sideGap = 10;
      const requiredWidth = detailPanelKind ? DETAIL_W : MENU_W;
      const roomRight = safeRight - bubbleRectAdj.right - sideGap;
      const roomLeft = bubbleRectAdj.left - safeLeft - sideGap;
      const useRight = roomRight >= requiredWidth && roomRight >= roomLeft;
      const useLeft = !useRight && roomLeft >= requiredWidth;

      if (useRight || useLeft) {
        const sideLeft = useRight
          ? bubbleRectAdj.right + sideGap
          : bubbleRectAdj.left - sideGap;
        const menuLeft = useRight ? sideLeft : sideLeft - MENU_W;
        const sidePanelClearance = detailPanelKind
          ? Math.max(MENU_H, Math.min(180, safeBottom - safeTop))
          : MENU_H;
        const menuTop = clamp(
          bubbleRectAdj.bottom - MENU_H,
          safeTop,
          safeBottom - sidePanelClearance
        );
        const reactionLeft = clamp(
          useRight ? sideLeft : sideLeft - REACTION_W,
          safeLeft,
          Math.max(safeLeft, safeRight - REACTION_W)
        );
        const reactionAbove = menuTop - REACTION_H - GAP;
        const reactionBelow = menuTop + MENU_H + GAP;
        const reactionTop = reactionAbove >= safeTop
          ? reactionAbove
          : reactionBelow + REACTION_H <= safeBottom
            ? reactionBelow
            : clamp(reactionAbove, safeTop, safeBottom - REACTION_H);

        let detailLeft = menuLeft;
        let detailTop = menuTop;
        let detailMaxHeight = Math.min(DETAIL_H, safeBottom - menuTop);
        if (detailPanelKind) {
          const besideMenu = useRight
            ? menuLeft + MENU_W + GAP
            : menuLeft - DETAIL_W - GAP;
          if (besideMenu >= safeLeft && besideMenu + DETAIL_W <= safeRight) {
            detailLeft = besideMenu;
          } else {
            // On narrower desktops the two panels form a stack in the side lane.
            detailLeft = clamp(
              useRight ? sideLeft : sideLeft - DETAIL_W,
              safeLeft,
              safeRight - DETAIL_W
            );
            detailMaxHeight = Math.min(
              DETAIL_H,
              Math.max(48, safeBottom - safeTop - MENU_H - GAP)
            );
            const stackTop = clamp(
              bubbleRectAdj.bottom - MENU_H - GAP - detailMaxHeight,
              safeTop,
              safeBottom - MENU_H - GAP - detailMaxHeight
            );
            detailTop = stackTop;
            // Keep the actions next to the selected message's lower edge.
            const stackedMenuTop = stackTop + detailMaxHeight + GAP;
            return {
              rowRect, actions, canReact, MENU_W, REACTION_W, REACTION_H,
              DETAIL_W, DETAIL_H, MENU_H, msgTop, msgLeft,
              msgMaxHeight: availableMessageHeight,
              msgIsScrollable: rowRect.height > availableMessageHeight,
              reactionTop, reactionLeft, menuTop: stackedMenuTop, menuLeft,
              detailTop, detailLeft, detailMaxHeight,
              detailTranslateY: false, detailPanelOpen: true,
            };
          }
        }

        return {
          rowRect, actions, canReact, MENU_W, REACTION_W, REACTION_H,
          DETAIL_W, DETAIL_H, MENU_H, msgTop, msgLeft,
          msgMaxHeight: availableMessageHeight,
          msgIsScrollable: rowRect.height > availableMessageHeight,
          reactionTop, reactionLeft, menuTop, menuLeft,
          detailTop, detailLeft, detailMaxHeight,
          detailTranslateY: false, detailPanelOpen: !!detailPanelKind,
        };
      }
    }

    // Menu aligns to the right edge of the *bubble* (in overlay coords)
    const menuLeft = clamp(
      bubbleRectAdj.right - MENU_W,
      safeLeft,
      safeRight - MENU_W
    );

    // Prefer placing menu under the bubble; if it doesn't fit, place it above.
    // Also ensure it never overlaps the bubble (even after clamping).
    const preferredBelow = bubbleRectAdj.bottom + GAP;
    const preferredAbove = bubbleRectAdj.top - GAP - MENU_H;

    const belowFits = preferredBelow + MENU_H <= safeBottom;
    const aboveFits = preferredAbove >= safeTop;

    const spaceBelow = safeBottom - (bubbleRectAdj.bottom + GAP);
    const spaceAbove = (bubbleRectAdj.top - GAP) - safeTop;

    let menuTop;
    let menuPlacement = "below";
    if (belowFits) {
      menuTop = preferredBelow;
      menuPlacement = "below";
    } else if (aboveFits) {
      menuTop = preferredAbove;
      menuPlacement = "above";
    } else {
      // Neither side fits fully: choose the side with more available space, then clamp.
      menuPlacement = spaceBelow >= spaceAbove ? "below" : "above";
      menuTop =
        menuPlacement === "below"
          ? clamp(preferredBelow, safeTop, safeBottom - MENU_H)
          : clamp(preferredAbove, safeTop, safeBottom - MENU_H);

      // Final guard: avoid overlap with the bubble.
      if (
        menuTop < bubbleRectAdj.bottom + GAP &&
        menuTop + MENU_H > bubbleRectAdj.top - GAP
      ) {
        menuTop =
          menuPlacement === "below"
            ? clamp(bubbleRectAdj.bottom + GAP, safeTop, safeBottom - MENU_H)
            : clamp(bubbleRectAdj.top - GAP - MENU_H, safeTop, safeBottom - MENU_H);
      }
    }

    const reactionLeft = clamp(
      bubbleRectAdj.left,
      safeLeft,
      safeRight - REACTION_W
    );
    const preferredReactionPlacement = menuPlacement === "below" ? "above" : "below";
    const preferredReactionBelow = bubbleRectAdj.bottom + REACTION_GAP;
    const preferredReactionAbove = bubbleRectAdj.top - REACTION_GAP - REACTION_H;
    const reactionBelowFits = preferredReactionBelow + REACTION_H <= safeBottom;
    const reactionAboveFits = preferredReactionAbove >= safeTop;
    const reactionSpaceBelow = safeBottom - (bubbleRectAdj.bottom + REACTION_GAP);
    const reactionSpaceAbove = (bubbleRectAdj.top - REACTION_GAP) - safeTop;

    let reactionPlacement = preferredReactionPlacement;
    let reactionTop;

    if (preferredReactionPlacement === "below") {
      if (reactionBelowFits) {
        reactionTop = preferredReactionBelow;
      } else if (reactionAboveFits) {
        reactionPlacement = "above";
        reactionTop = preferredReactionAbove;
      } else {
        reactionPlacement = reactionSpaceBelow >= reactionSpaceAbove ? "below" : "above";
        reactionTop =
          reactionPlacement === "below"
            ? clamp(preferredReactionBelow, safeTop, safeBottom - REACTION_H)
            : clamp(preferredReactionAbove, safeTop, safeBottom - REACTION_H);
      }
    } else if (reactionAboveFits) {
      reactionTop = preferredReactionAbove;
    } else if (reactionBelowFits) {
      reactionPlacement = "below";
      reactionTop = preferredReactionBelow;
    } else {
      reactionPlacement = reactionSpaceAbove >= reactionSpaceBelow ? "above" : "below";
      reactionTop =
        reactionPlacement === "above"
          ? clamp(preferredReactionAbove, safeTop, safeBottom - REACTION_H)
          : clamp(preferredReactionBelow, safeTop, safeBottom - REACTION_H);
    }

    if (
      reactionTop < bubbleRectAdj.bottom + REACTION_GAP &&
      reactionTop + REACTION_H > bubbleRectAdj.top - REACTION_GAP
    ) {
      if (reactionPlacement === "below" && reactionAboveFits) {
        reactionPlacement = "above";
        reactionTop = preferredReactionAbove;
      } else if (reactionPlacement === "above" && reactionBelowFits) {
        reactionPlacement = "below";
        reactionTop = preferredReactionBelow;
      }
    }

    // Ensure actions and reactions never overlap each other.
    // UX preference: move actions UP first, then reactions as fallback.
    const MENU_REACTION_GAP = 6;
    const panelsOverlapHorizontally =
      menuLeft < reactionLeft + REACTION_W && menuLeft + MENU_W > reactionLeft;
    const panelsOverlapVertically =
      menuTop < reactionTop + REACTION_H + MENU_REACTION_GAP &&
      menuTop + MENU_H + MENU_REACTION_GAP > reactionTop;

    if (!detailPanelKind && panelsOverlapHorizontally && panelsOverlapVertically) {
      const preferredRaisedMenuTop = reactionTop - MENU_REACTION_GAP - MENU_H;
      const canRaiseMenu = preferredRaisedMenuTop >= safeTop;

      if (canRaiseMenu) {
        menuTop = preferredRaisedMenuTop;
        menuPlacement = "above";
      } else {
        const preferredLowerReactionTop = menuTop + MENU_H + MENU_REACTION_GAP;
        const canLowerReactions =
          preferredLowerReactionTop + REACTION_H <= safeBottom;

        if (canLowerReactions) {
          reactionTop = preferredLowerReactionTop;
          reactionPlacement = "below";
        } else {
          // Last fallback: raise menu as much as possible.
          menuTop = clamp(
            reactionTop - MENU_REACTION_GAP - MENU_H,
            safeTop,
            safeBottom - MENU_H
          );
        }
      }
    }

    // Secondary detail panel (seen / reactions):
    // 1) prefer side-by-side with the actions menu
    // 2) on narrow screens, stack it above/below the menu instead of overlapping it
    const preferredDetailLeft = menuLeft - GAP - DETAIL_W;
    const preferredSeenRight = menuLeft + MENU_W + GAP;
    const canPlaceDetailLeft = preferredDetailLeft >= safeLeft;
    const canPlaceDetailRight = preferredSeenRight + DETAIL_W <= safeRight;

    let detailLeft = menuLeft;
    let detailTop = menuTop;
    let detailMaxHeight = DETAIL_H;
    let detailTranslateY = false;

    if (detailPanelKind) {
      if (canPlaceDetailLeft) {
        detailLeft = preferredDetailLeft;
        detailTop = menuTop;
      } else if (canPlaceDetailRight) {
        detailLeft = preferredSeenRight;
        detailTop = menuTop;
      } else {
        detailLeft = clamp(
          menuLeft + MENU_W - DETAIL_W,
          safeLeft,
          safeRight - DETAIL_W
        );

        // Mobile: treat details + actions as one compact stack. The actions stay
        // closest to the selected bubble and the details panel is anchored by its
        // real rendered edge, so a short reactions list never leaves a phantom
        // DETAIL_H-sized gap between the two surfaces.
        const MIN_DETAIL_H = 48;
        const detailRoomAbove =
          bubbleRectAdj.top - GAP - safeTop - MENU_H - GAP;
        const detailRoomBelow =
          safeBottom - (bubbleRectAdj.bottom + GAP) - MENU_H - GAP;
        const preferredStackPlacement = menuPlacement;
        const preferredRoom =
          preferredStackPlacement === "above" ? detailRoomAbove : detailRoomBelow;
        const alternateRoom =
          preferredStackPlacement === "above" ? detailRoomBelow : detailRoomAbove;
        const stackPlacement =
          preferredRoom >= MIN_DETAIL_H || preferredRoom >= alternateRoom
            ? preferredStackPlacement
            : preferredStackPlacement === "above"
              ? "below"
              : "above";

        if (stackPlacement === "above") {
          menuTop = bubbleRectAdj.top - GAP - MENU_H;
          detailTop = menuTop - GAP;
          detailTranslateY = true;
          detailMaxHeight = Math.max(
            MIN_DETAIL_H,
            Math.min(DETAIL_H, detailRoomAbove)
          );
        } else {
          menuTop = bubbleRectAdj.bottom + GAP;
          detailTop = menuTop + MENU_H + GAP;
          detailMaxHeight = Math.max(
            MIN_DETAIL_H,
            Math.min(DETAIL_H, detailRoomBelow)
          );
        }
      }
    }

    return {
      rowRect,
      actions,
      canReact,
      MENU_W,
      REACTION_W,
      REACTION_H,
      DETAIL_W,
      DETAIL_H,
      MENU_H,
      msgTop,
      msgLeft,
      msgMaxHeight: availableMessageHeight,
      msgIsScrollable: rowRect.height > availableMessageHeight,
      reactionTop,
      reactionLeft,
      menuTop,
      menuLeft,
      detailTop,
      detailLeft,
      detailMaxHeight,
      detailTranslateY,
      detailPanelOpen: !!detailPanelKind,
    };
  }, [
    hasCtx,
    ctxMenu,
    t,
    copyMessage,
    beginReply,
    beginEdit,
    canEdit,
    canDelete,
    onCloseCtxMenu,
    getHeaderBottomPx,
    deleteMessage,
    onTogglePin,
    pinnedMessageId,
    pinnedMessageIds,
    onToggleSeen,
    isDirect,
    detailPanelKind,
    ensureReactionDetails,
    messageReactions,
    seenCount,
    restrictBackdropToParent,
  ]);

  // A context-menu request can outlive the DOM node it was measured from
  // (for example when a realtime update replaces the row during a long press).
  // In that state the chat used to disable its own pointer events while this
  // component rendered no dismissible overlay, which made the whole screen
  // appear frozen. Fail closed instead of leaving an invisible interaction lock.
  useEffect(() => {
    if (!hasCtx || ctxLayout) return undefined;

    const timeoutId = window.setTimeout(() => onCloseCtxMenu?.(), 0);
    return () => window.clearTimeout(timeoutId);
  }, [ctxLayout, hasCtx, onCloseCtxMenu]);

  // Native WebViews may background the app while a transient layer is open.
  // Clear those layers on background/resume so an interrupted gesture cannot
  // strand a full-screen hit target over the chat after returning to the app.
  useEffect(() => {
    if (!hasCtx && !hasImage) return undefined;

    const closeTransientLayers = () => {
      onCloseCtxMenu?.();
      onCloseImageViewer?.();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") closeTransientLayers();
    };

    window.addEventListener("pagehide", closeTransientLayers);
    window.addEventListener("vg:native-resume", closeTransientLayers);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", closeTransientLayers);
      window.removeEventListener("vg:native-resume", closeTransientLayers);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [hasCtx, hasImage, onCloseCtxMenu, onCloseImageViewer]);

  // Now it's safe to return early (hooks already called)
  if (!hasCtx && !hasImage) return null;

  const selectedReactions = new Set(
    messageReactions.filter((item) => item?.is_self).map((item) => item.emoji)
  );

  const isSeenPanelLoading =
    detailPanelKind === "seen" &&
    String(seenPanel?.messageId) === String(currentMessageId) &&
    !!seenPanel?.loading;

  const isReactionPanelLoading =
    detailPanelKind === "reactions" &&
    String(reactionPanel?.messageId) === String(currentMessageId) &&
    !!reactionPanel?.loading;

  const renderOverlayUserRow = (user, key, showDivider = false) => {
    const username = (user?.username || user?.author_username || "").toString();
    const code = (user?.code || "").toString();
    const avatarSrc = buildOverlayAvatarSrc(user);

    return (
      <React.Fragment key={key}>
        <div className="w-full h-[34px] px-[10px] flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-full bg-white/20 overflow-hidden shrink-0 flex items-center justify-center">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt={code || username}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : null}
            </div>
            <span className="text-white text-xs font-medium truncate">
              {username ? `@${username}` : "@user"}
            </span>
          </div>
          <PremiumCodeLabel
            code={code}
            isPremium={Boolean(user?.has_premium_subscription)}
            className="text-white/85 text-xs font-black shrink-0 ml-2"
          />
        </div>
        {showDivider ? <div className="h-px bg-white/20" /> : null}
      </React.Fragment>
    );
  };

  const mobileDetailPanel = detailPanelKind ? (
    <div
      data-chat-overlay-section="details"
      data-testid="chat-overlay-details"
      className="pointer-events-auto w-36 select-none overflow-hidden rounded-2xl bg-zinc-800/95 shadow-[0_14px_32px_rgba(0,0,0,0.3)]"
      style={{ maxHeight: "min(22dvh, 180px)" }}
    >
      <div className="overflow-y-auto" style={{ maxHeight: "min(22dvh, 180px)" }}>
        {detailPanelKind === "seen" ? (
          isSeenPanelLoading ? (
            <div className="flex min-h-[60px] items-center justify-center px-3">
              <span className="text-xs font-medium text-white/80">
                {t("chats.event_chat.seen.loading")}
              </span>
            </div>
          ) : !seenUsers.length ? (
            <div className="flex min-h-[60px] items-center justify-center px-3">
              <span className="text-center text-xs font-medium text-white/70">
                {t("chats.event_chat.seen.empty")}
              </span>
            </div>
          ) : (
            seenUsers.map((user, index) =>
              renderOverlayUserRow(
                user,
                user?.id || `${user?.username || "user"}-${user?.code || index}`,
                index < seenUsers.length - 1
              )
            )
          )
        ) : isReactionPanelLoading ? (
          <div className="flex min-h-[60px] items-center justify-center px-3">
            <span className="text-xs font-medium text-white/80">
              {t("chats.event_chat.reactions.loading")}
            </span>
          </div>
        ) : !visibleReactionGroups.length ? (
          <div className="flex min-h-[60px] items-center justify-center px-3">
            <span className="text-center text-xs font-medium text-white/70">
              {t("chats.event_chat.reactions.empty")}
            </span>
          </div>
        ) : (
          visibleReactionGroups.map((group, groupIndex) => {
            const users = Array.isArray(group?.users) ? group.users : [];
            return (
              <div
                key={`${group?.emoji || "reaction"}-${groupIndex}`}
                className={groupIndex > 0 ? "border-t border-white/12" : ""}
              >
                <div className="px-3 py-2">
                  <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2.5 text-xs font-semibold text-white">
                    <span className="text-[15px] leading-none">{group?.emoji}</span>
                    {Number(group?.count || 0) > 1 ? <span>{group.count}</span> : null}
                  </span>
                </div>
                {users.length ? (
                  users.map((user, userIndex) =>
                    renderOverlayUserRow(
                      user,
                      user?.id ||
                        `${group?.emoji || "reaction"}-${user?.username || "user"}-${
                          user?.code || userIndex
                        }`,
                      userIndex < users.length - 1
                    )
                  )
                ) : (
                  <div className="px-3 pb-3">
                    <span className="text-[11px] font-medium text-white/60">
                      {t("chats.event_chat.reactions.empty_group")}
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  const overlayContent = (
    <>
      {/* =========================
          Context menu overlay
         ========================= */}
      {hasCtx && ctxLayout && isMobileContextLayer ? (
        <MobileMessageActionLayer
          item={ctxMenu.item}
          rowRect={ctxMenu.rowRect || ctxMenu.rect}
          actions={ctxLayout.actions}
          canReact={ctxLayout.canReact && !ctxLayout.detailPanelOpen}
          selectedReactions={selectedReactions}
          onToggleReaction={onToggleReaction}
          onClose={onCloseCtxMenu}
          variant={variant}
          detailPanel={mobileDetailPanel}
          t={t}
        />
      ) : null}

      {hasCtx && ctxLayout && !isMobileContextLayer ? (
        <>
          {/* Full-screen backdrop; covers bottom nav while message actions are open. */}
          <div
            data-testid="chat-context-backdrop"
            className={`fixed touch-manipulation ${
              restrictBackdropToParent ? "z-[90]" : "z-[95]"
            } overflow-hidden`}
            style={
              restrictBackdropToParent
                ? {
                    left: ctxMenu?.overlayScopeRect?.left ?? 0,
                    top: ctxMenu?.overlayScopeRect?.top ?? 0,
                    width: ctxMenu?.overlayScopeRect?.width ?? "100vw",
                    height: ctxMenu?.overlayScopeRect?.height ?? "100vh",
                  }
                : { inset: 0, backgroundColor: "rgba(0, 0, 0, 0.45)" }
            }
            onPointerDown={onCloseCtxMenu}
          >
            {restrictBackdropToParent ? (
              <>
                <div
                  data-testid="chat-context-backdrop-body"
                  className="pointer-events-none absolute bg-black/45"
                  style={{
                    left:
                      ctxMenu?.overlayBodyRect && ctxMenu?.overlayScopeRect
                        ? ctxMenu.overlayBodyRect.left - ctxMenu.overlayScopeRect.left
                        : 0,
                    top:
                      ctxMenu?.overlayBodyRect && ctxMenu?.overlayScopeRect
                        ? ctxMenu.overlayBodyRect.top - ctxMenu.overlayScopeRect.top
                        : 0,
                    width: ctxMenu?.overlayBodyRect?.width ?? ctxMenu?.overlayScopeRect?.width ?? "100vw",
                    height: ctxMenu?.overlayBodyRect?.height ?? ctxMenu?.overlayScopeRect?.height ?? "100vh",
                    borderRadius: 45,
                  }}
                  aria-hidden="true"
                />
                {ctxMenu?.overlayHeaderRect && ctxMenu?.overlayBodyRect ? (
                  <div
                    data-testid="chat-context-backdrop-header"
                    className="pointer-events-none absolute bg-black/45"
                    style={{
                      left:
                        ctxMenu.overlayHeaderRect.left -
                        (ctxMenu?.overlayScopeRect?.left ?? 0),
                      top:
                        ctxMenu.overlayHeaderRect.top -
                        (ctxMenu?.overlayScopeRect?.top ?? 0),
                      width: ctxMenu.overlayHeaderRect.width,
                      height: ctxMenu.overlayHeaderRect.height,
                      borderRadius: ctxMenu.overlayHeaderBorderRadius || 0,
                      clipPath: `inset(0 0 ${Math.max(
                        0,
                        ctxMenu.overlayHeaderRect.bottom -
                          (ctxMenu?.overlayBodyRect?.top ?? ctxMenu.overlayHeaderRect.bottom)
                      )}px 0)`,
                    }}
                    aria-hidden="true"
                  />
                ) : null}
              </>
            ) : null}
          </div>

          {/* Cloned message (sharp). For direct chats, hide avatar badge */}
          <div
            data-testid="chat-overlay-selected-message-desktop"
            className="fixed z-[100]"
            style={{
              left: ctxLayout.msgLeft,
              top: ctxLayout.msgTop,
              width: ctxLayout.rowRect.width,
              maxHeight: ctxLayout.msgMaxHeight,
              overflowY: ctxLayout.msgIsScrollable ? "auto" : "visible",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <MessageBubble
              item={ctxMenu.item}
              disableInteractions
              variant={variant}
              hideSenderBadge={variant === "direct"}
            />
          </div>

          {ctxLayout.canReact && !ctxLayout.detailPanelOpen && (
            <div
              className="fixed z-[110]"
              style={{
                left: ctxLayout.reactionLeft,
                top: ctxLayout.reactionTop,
                width: ctxLayout.REACTION_W,
                height: ctxLayout.REACTION_H,
              }}
            >
              <div className="flex h-full items-center justify-center gap-1 rounded-[22px] bg-zinc-800/80 px-2 shadow-[0_12px_30px_rgba(0,0,0,0.28)]">
                {CHAT_REACTION_OPTIONS.map((option) => {
                  const isSelected = selectedReactions.has(option.emoji);
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => {
                        onToggleReaction?.(ctxMenu?.item, option.emoji);
                        onCloseCtxMenu?.();
                      }}
                      className={`flex h-8 w-8 items-center justify-center rounded-full text-[18px] transition active:scale-[0.96] ${
                        isSelected ? "bg-[#3FA3FF]" : "bg-white/10"
                      }`}
                      aria-label={t(option.labelKey)}
                    >
                      <span aria-hidden="true">{option.emoji}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Detail panel (seen / reactions). */}
          {ctxLayout.detailPanelOpen ? (
            <div
              className="fixed z-[110]"
              data-chat-overlay-section="details"
              data-testid="chat-overlay-details"
              style={{
                left: ctxLayout.detailLeft,
                top: ctxLayout.detailTop,
                width: ctxLayout.DETAIL_W,
                maxHeight: ctxLayout.detailMaxHeight,
                transform: ctxLayout.detailTranslateY
                  ? "translateY(-100%)"
                  : undefined,
              }}
            >
              <div
                className="relative select-none overflow-hidden rounded-2xl bg-zinc-800/70"
                style={{ maxHeight: ctxLayout.detailMaxHeight }}
              >
                <div
                  className="overflow-y-auto"
                  style={{ maxHeight: ctxLayout.detailMaxHeight }}
                >
                  {detailPanelKind === "seen" ? (
                    isSeenPanelLoading ? (
                      <div className="min-h-[60px] flex items-center justify-center px-3">
                        <span className="text-white/80 text-xs font-medium">
                          {t("chats.event_chat.seen.loading")}
                        </span>
                      </div>
                    ) : !seenUsers.length ? (
                      <div className="min-h-[60px] flex items-center justify-center px-3">
                        <span className="text-white/70 text-xs font-medium text-center">
                          {t("chats.event_chat.seen.empty")}
                        </span>
                      </div>
                    ) : (
                      seenUsers.map((user, index) =>
                        renderOverlayUserRow(
                          user,
                          user?.id || `${user?.username || "user"}-${user?.code || index}`,
                          index < seenUsers.length - 1
                        )
                      )
                    )
                  ) : isReactionPanelLoading ? (
                    <div className="min-h-[60px] flex items-center justify-center px-3">
                      <span className="text-white/80 text-xs font-medium">
                        {t("chats.event_chat.reactions.loading")}
                      </span>
                    </div>
                  ) : !visibleReactionGroups.length ? (
                    <div className="min-h-[60px] flex items-center justify-center px-3">
                      <span className="text-white/70 text-xs font-medium text-center">
                        {t("chats.event_chat.reactions.empty")}
                      </span>
                    </div>
                  ) : (
                    visibleReactionGroups.map((group, groupIndex) => {
                      const users = Array.isArray(group?.users) ? group.users : [];
                      return (
                        <div
                          key={`${group?.emoji || "reaction"}-${groupIndex}`}
                          className={groupIndex > 0 ? "border-t border-white/12" : ""}
                        >
                          <div className="px-3 py-2">
                            <span className="inline-flex h-7 items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-2.5 text-xs font-semibold text-white">
                              <span className="text-[15px] leading-none">{group?.emoji}</span>
                              {Number(group?.count || 0) > 1 ? (
                                <span>{group.count}</span>
                              ) : null}
                            </span>
                          </div>
                          {users.length ? (
                            users.map((user, userIndex) =>
                              renderOverlayUserRow(
                                user,
                                user?.id ||
                                  `${group?.emoji || "reaction"}-${user?.username || "user"}-${user?.code || userIndex}`,
                                userIndex < users.length - 1
                              )
                            )
                          ) : (
                            <div className="px-3 pb-3">
                              <span className="text-white/60 text-[11px] font-medium">
                                {t("chats.event_chat.reactions.empty_group")}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          ) : null}

          {/* Actions menu (sharp). Reactions stay on the opposite side of the bubble. */}
          <div
            className="fixed z-[110]"
            data-chat-overlay-section="actions"
            data-testid="chat-overlay-actions"
            style={{
              left: ctxLayout.menuLeft,
              top: ctxLayout.menuTop,
              width: ctxLayout.MENU_W,
            }}
          >
            <div className="w-36 relative select-none overflow-hidden rounded-2xl bg-zinc-800/70">
              {ctxLayout.actions.map((a, idx) => (
                <React.Fragment key={a.key}>
                  <button
                    type="button"
                    onClick={a.onClick}
                    className="w-full h-[30px] px-[10px] flex items-center justify-between"
                    aria-label={a.label}
                  >
                    <span
                      className={`${
                        a.danger ? "text-red-500" : "text-white"
                      } text-xs font-medium leading-5`}
                    >
                      {a.label}
                    </span>

                    {a.icon ? (
                      <img src={a.icon} alt="" className="w-4 h-4 opacity-90" />
                    ) : a.iconNode ? (
                      <span className="w-4 h-4 flex items-center justify-center">
                        {a.iconNode}
                      </span>
                    ) : null}
                  </button>

                  {idx < ctxLayout.actions.length - 1 && (
                    <div className="h-px bg-white/20" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {/* =========================
          Image viewer overlay
         ========================= */}
      {hasImage && (
        <>
          <div
            className="fixed inset-0 bg-black/45 touch-manipulation z-[100]"
            onPointerDown={onCloseImageViewer}
          />
          <ImageViewerLayer
            src={imageViewer.src}
            onClose={onCloseImageViewer}
            canDelete={canDeleteImage}
            onDelete={async () => {
              if (!canDeleteImage) return false;
              const deleted = await deleteMessage?.(imageMessage);
              if (deleted) onCloseImageViewer?.();
              return deleted;
            }}
            saveLabel={t("chats.event_chat.image_viewer.save", { defaultValue: "Save" })}
            deleteLabel={t("chats.event_chat.image_viewer.delete", { defaultValue: "Delete photo" })}
            closeLabel={t("chats.event_chat.image_viewer.close", { defaultValue: "Close photo" })}
          />
        </>
      )}
    </>
  );

  // Desktop chat views animate their centered workspace with transforms. A fixed
  // descendant of that workspace would therefore use it as a containing block and
  // apply the workspace offset twice. Portaling keeps all measured viewport
  // coordinates stable while the backdrop still follows the scoped chat rectangle.
  return typeof document !== "undefined"
    ? createPortal(overlayContent, document.body)
    : overlayContent;
}

// ===== IMAGE VIEWER LAYER (Telegram-like, swipe-down-to-close) =====
const IMAGE_VIEWER_ACTION_BUTTON_CLASS =
  "flex h-9 w-9 items-center justify-center rounded-full border border-white/25 bg-white/15 text-white backdrop-blur-md transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-55";

function ImageViewerLayer({
  src,
  onClose,
  canDelete = false,
  onDelete = null,
  saveLabel = "Save",
  deleteLabel = "Delete photo",
  closeLabel = "Close photo",
}) {
  const startYRef = useRef(null);
  const deltaYRef = useRef(0);
  const isIOSRef = useRef(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    try {
      isIOSRef.current = /iPhone|iPad|iPod/i.test(
        String(window?.navigator?.userAgent || "")
      );
    } catch {
      isIOSRef.current = false;
    }
  }, []);

  const getFileName = (rawUrl) => {
    try {
      const u = new URL(String(rawUrl || ""), window.location.origin);
      const p = (u.pathname || "").split("/").filter(Boolean);
      const tail = p[p.length - 1] || "";
      if (tail) return tail;
    } catch {
      // ignore
    }
    return `chat-image-${Date.now()}.jpg`;
  };

  const triggerDownload = (url, filename, openInNewTab = false) => {
    try {
      const a = document.createElement("a");
      a.href = url;
      if (openInNewTab) {
        a.target = "_blank";
        a.rel = "noopener noreferrer";
      } else {
        a.download = filename || "image";
      }
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      // ignore
    }
  };

  const handleSave = async (e) => {
    try {
      e?.stopPropagation?.();
      e?.preventDefault?.();
    } catch {
      // ignore
    }

    // iOS Safari often ignores the `download` attribute for images.
    // Opening image URL in a new tab allows native "Save to Photos".
    if (isIOSRef.current) {
      triggerDownload(src, "", true);
      return;
    }

    const filename = getFileName(src);
    try {
      const resp = await fetch(src, {
        credentials: "include",
        mode: "cors",
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, filename, false);
      window.setTimeout(() => {
        try {
          URL.revokeObjectURL(blobUrl);
        } catch {
          // ignore
        }
      }, 20_000);
    } catch {
      // Fallback when fetch/CORS fails.
      triggerDownload(src, filename, false);
    }
  };

  const handleDelete = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!canDelete || deleting || typeof onDelete !== "function") return;

    setDeleting(true);
    try {
      await onDelete();
    } finally {
      setDeleting(false);
    }
  };

  const onTouchStart = (e) => {
    try {
      const y = e?.touches?.[0]?.clientY;
      if (typeof y === "number") {
        startYRef.current = y;
        deltaYRef.current = 0;
      }
    } catch {
      // ignore
    }
  };

  const onTouchMove = (e) => {
    try {
      const y = e?.touches?.[0]?.clientY;
      if (typeof y === "number" && typeof startYRef.current === "number") {
        deltaYRef.current = y - startYRef.current;
      }
    } catch {
      // ignore
    }
  };

  const onTouchEnd = () => {
    const dy = deltaYRef.current;
    startYRef.current = null;
    deltaYRef.current = 0;

    if (dy > 80) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center"
      style={{ padding: 16 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute right-[calc(env(safe-area-inset-right,0px)+16px)] top-[calc(var(--vg-safe-area-top)+16px)] z-[120] flex items-center gap-2">
        {canDelete ? (
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            aria-busy={deleting}
            className={IMAGE_VIEWER_ACTION_BUTTON_CLASS}
            aria-label={deleteLabel}
            title={deleteLabel}
          >
            <img
              src="/assets/menu/chat_ctx_delete.svg"
              alt=""
              aria-hidden="true"
              className={`h-5 w-5 brightness-0 invert ${deleting ? "opacity-70" : ""}`}
              draggable={false}
            />
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleSave}
          className={IMAGE_VIEWER_ACTION_BUTTON_CLASS}
          aria-label={saveLabel}
          title={saveLabel}
        >
          <img
            src="/assets/menu/download.svg"
            alt=""
            aria-hidden="true"
            className="h-5 w-5 brightness-0 invert"
            draggable={false}
          />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className={`${IMAGE_VIEWER_ACTION_BUTTON_CLASS} text-lg leading-none`}
          aria-label={closeLabel}
          title={closeLabel}
        >
          ×
        </button>
      </div>
      <img
        src={src}
        alt=""
        className="max-w-full max-h-full object-contain rounded-xl"
        style={{
          WebkitTouchCallout: "default",
          WebkitUserSelect: "auto",
          userSelect: "auto",
        }}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}
